import { domainError, err, ok } from '@flourprint/core/domain';
const copy = (value) => JSON.parse(JSON.stringify(value));
/**
 * In-memory personal ingredient entries, customizations included; same contract as the SQLite
 * implementations. Versions of an id may have gaps: an import writes only the versions a recipes
 * file pins (ingredients.md).
 */
export class MemoryIngredientRegistry {
    /** Versions of each id, by version number. */
    #entries = new Map();
    #versions(id) {
        return [...(this.#entries.get(id)?.values() ?? [])].sort((a, b) => a.version - b.version);
    }
    async listIngredients() {
        return [...this.#entries.keys()]
            .sort()
            .flatMap((id) => this.#versions(id).slice(-1))
            .map(copy);
    }
    async getIngredient(id, version) {
        const entry = this.#entries.get(id)?.get(version);
        return ok(entry && copy(entry));
    }
    async listIngredientVersions() {
        return [...this.#entries.keys()]
            .sort()
            .flatMap((id) => this.#versions(id))
            .map(copy);
    }
    async saveIngredient(entry) {
        const currentHead = this.#versions(entry.id).at(-1)?.version ?? 0;
        if (entry.version !== currentHead + 1) {
            return err(domainError('HeadConflict', `Ingredient ${entry.id} is at version ${currentHead}`, {
                ingredientId: entry.id,
                currentHead,
            }));
        }
        this.write(entry);
        return ok(copy(entry));
    }
    /** Whether that version is stored (imports skip it). */
    has(id, version) {
        return this.#entries.get(id)?.has(version) ?? false;
    }
    /** Writes a checked version synchronously (restores and imports). */
    write(entry) {
        const versions = this.#entries.get(entry.id) ?? new Map();
        versions.set(entry.version, copy(entry));
        this.#entries.set(entry.id, versions);
    }
    get isEmpty() {
        return this.#entries.size === 0;
    }
}
//# sourceMappingURL=ingredient-registry.js.map